import { Routes, Route } from 'react-router-dom';
import { useSelector } from 'react-redux';

// Layouts
import PublicLayout from './AppLayoutsPublic';
import AppLayout from './AppLayout';
import SemiPublicLayout from './SemiPublicLayout';

// Util
import NotFoundPage from './pages/Error404/route404Page';
import PrivateRoute from './PrivateRoutes';
import PrivateAdminRoute from './routesAdmin';

// Public pages
import HomePage from './pages/home/HomePage';
import PricingPage from './pages/Pricing/PricingPage';
import FeaturesPage from './pages/FeaturesPage/FeaturesPage';
import RegisterPage from './pages/Register/RegisterPage';
import LoginPage from './pages/Login/LoginPage';
import ContactPage from './pages/ContactPages/ContactPage';
import ForgotPasswordPage from './pages/Login/ForgotPasswordPage';
import ResetPasswordPage from './pages/Login/ResetPasswordPage';
import VerifyEmailPage from './pages/Login/VerifyEmailPage';
import OAuthCallbackPage from './pages/Login/OAuthCallbackPage';
import MFACodeEntryPage from './pages/Login/MFACodeEntryPage';
import { PrivacyPage, TermsPage, DisclaimerPage } from './pages/LegalPages/LegalPage';
import PublicProfilePage from './pages/PublicProfile/publicProfileInUse';

// App pages (authenticated)
import UnifiedStockerDashboard from './pages/Dashboard/UnifiedStockerDashboard';
import Stockers from './pages/Stockers/Stockers';
import ProfilePage from './pages/MyAccount/profile';
import StocksOverviewPage from './pages/Stocks/StockOverviewPage';
import StockDetailPage from './pages/Stocks/StockDetail';
import WatchlistPage from './pages/Watchlist/WatchlistPage';
import PortfolioPage from './pages/Portfolio/PortfolioPage';
import PostForm from './pages/Stockers/PostForm';
import NotificationCenter from './pages/notificationCenter/notificationCenterPage';
import GroupListPage from './pages/Groups/groupsMainPage/GroupListPage';
import GroupDetailPage from './pages/Groups/groupsMainPage/GroupDetailPage';
import GroupCreate from './pages/Groups/groupsMainPage/GroupCreate';
import ConnectionsPage from './pages/ConnectionsPage/ConnectionsPage';
import LeaderboardPage from './pages/Leaderboard/LeaderboardPage';
import CredentialQuizPage from './pages/CredentialQuiz/CredentialQuizPage';
import NewsFeedPage from './pages/News/NewsFeedPage';
import NewsArticleDetailPage from './pages/News/NewsArticleDetailPage';
import NewsSubmitPage from './pages/News/NewsSubmitPage';

// Settings
import MainSettingsPage from './pages/settingsPages/mainSettingsPage';
import LanguageSettingsPage from './pages/settingsPages/languagePage/languageSettingsPage';
import AccessibilitySettingsPage from './pages/settingsPages/accessibilityPage/accessibilitySettingsPage';
import HelpCenterPage from './pages/settingsPages/helpCenterPage/helpCenterPage';
import AccountDataPage from './pages/settingsPages/userDataPage/userDataPage';
import SubscriptionsPage from './pages/settingsPages/subscriptionsPage/subscriptionsPage';
import CreatorDashboardPage from './pages/Creator/CreatorDashboardPage';
import NotificationsSettingsPage from './pages/settingsPages/notificationsPage/notificationsSettingsPage';
import SecuritySettingsPage from './pages/settingsPages/securityPage/securitySettingsPage';

// Admin
import AdminDashboard from './pages/adminDashboard/adminDashboard';

const AppRoutes = () => {
  const featureFlags = useSelector((state) => state.featureFlags.flags);
  return (
    <Routes>

      {/* 🌍 PUBLIC */}
      <Route element={<PublicLayout />}>
        <Route path="/" element={<HomePage />} />
        <Route path="/pricing" element={<PricingPage />} />
        <Route path="/features" element={<FeaturesPage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/disclaimer" element={<DisclaimerPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
        <Route path="/verify-email/:token" element={<VerifyEmailPage />} />
        <Route path="/oauth-callback" element={<OAuthCallbackPage />} />
        <Route path="/login/mfa" element={<MFACodeEntryPage />} />
      </Route>

      {/* Reachable by anonymous visitors (public profiles are, by design,
          public) but also a constant in-app destination for logged-in
          users -- SemiPublicLayout gives them full app chrome instead of
          stranding them with zero navigation. */}
      <Route element={<SemiPublicLayout />}>
        <Route path="/public-profiles/:slug" element={<PublicProfilePage />} />
      </Route>

      {/* 🔐 AUTHENTICATED APP (SINGLE SCROLL OWNER) */}
      <Route
        element={
          <PrivateRoute>
            <AppLayout />
          </PrivateRoute>
        }
      >
        {/* App */}
        <Route path="/dashboard" element={<UnifiedStockerDashboard />} />
        {/* Posts feature is off (featureFlags.posts) -- these routes still
            existed and were directly reachable (bookmark/typed URL/back
            button) even with the nav links hidden, so they must 404 too. */}
        <Route path="/stockers" element={featureFlags.posts ? <Stockers /> : <NotFoundPage />} />
        <Route path="/profile" element={<ProfilePage />} />
        <Route path="/stocks-overview" element={<StocksOverviewPage />} />
        <Route path="/stocks/:symbol" element={<StockDetailPage />} />
        <Route path="/watchlist" element={<WatchlistPage />} />
        <Route path="/portfolio" element={<PortfolioPage />} />
        <Route path="/groupies" element={<GroupListPage />} />
        <Route path="/groups/:groupId" element={<GroupDetailPage />} />
        <Route path="/create-group" element={<GroupCreate />} />
        <Route path="/post-create" element={featureFlags.posts ? <PostForm /> : <NotFoundPage />} />
        <Route path="/notifications" element={<NotificationCenter />} />
        <Route path="/connections/me" element={<ConnectionsPage />} />
        <Route path="/leaderboard" element={<LeaderboardPage />} />
        <Route path="/knowledge-quiz" element={<CredentialQuizPage />} />
        <Route path="/news" element={<NewsFeedPage />} />
        <Route path="/news/submit" element={<NewsSubmitPage />} />
        <Route path="/news/:articleId" element={<NewsArticleDetailPage />} />
        <Route path="/creator/dashboard" element={<CreatorDashboardPage />} />
        {/* Settings */}
        <Route path="/settings" element={<MainSettingsPage />} />
        <Route path="/settings/language" element={<LanguageSettingsPage />} />
        <Route path="/settings/accessibility" element={<AccessibilitySettingsPage />} />
        <Route path="/settings/notifications" element={<NotificationsSettingsPage />} />
        <Route path="/settings/security" element={<SecuritySettingsPage />} />
        <Route path="/settings/help-center" element={<HelpCenterPage />} />
        <Route path="/settings/your-data" element={<AccountDataPage />} />
        <Route path="/settings/subscriptions" element={<SubscriptionsPage />} />

        {/* 🛡️ ADMIN — MUST BE HERE */}
        <Route
          path="/admin"
          element={
            <PrivateAdminRoute>
              <AdminDashboard />
            </PrivateAdminRoute>
          }
        />
      </Route>

      {/* ❌ 404 */}
      <Route path="*" element={<NotFoundPage />} />

    </Routes>
  );
};


export default AppRoutes;
